/** What a learner sees when the free part runs out.
 *
 * They have just opened one real lesson, or tried one real question, and
 * reached for the next. That is the best moment the product ever gets to
 * ask for money — they know what they are buying because they have just
 * used it.
 *
 * So this says what they already have rather than what they lack, and it
 * is a plain offer with one button. No countdown, no scarcity, no
 * "unlock" language: the thing being sold is a German course, and a
 * learner who feels pressured into buying one usually stops using it.
 *
 * Its copy lives under `taster.*`, NOT `locked.*`: that family already
 * belongs to ContentLocked, whose strings take {level} and {kind}
 * parameters. Reusing the names renders placeholders at a learner mid-way
 * through deciding to pay.
 */
import { Link } from "react-router-dom";
import { Card } from "../../components/ui";
import { useT } from "../../i18n";

export function LockedNotice({ kind = "lesson" }: { kind?: "lesson" | "item" }) {
  const t = useT();
  return (
    <Card>
      <div className="text-center">
        <h2 className="text-lg font-extrabold text-ink">{t("taster.title")}</h2>
        <p className="mx-auto mt-2 max-w-prose text-sm text-muted">
          {t(kind === "item" ? "taster.itemBody" : "taster.body")}
        </p>
        <Link
          to="/subscribe"
          className="mt-4 inline-block rounded-control bg-brand px-5 py-2.5 text-sm font-semibold text-white"
        >
          {t("taster.cta")}
        </Link>
      </div>
    </Card>
  );
}

/** A quiet badge on the one tile that is open to everyone.
 *
 *  Worth showing because the alternative is a learner assuming the whole
 *  course is free and feeling misled at the second lesson — the badge is
 *  the honest version of the same information. */
export function FreeBadge() {
  const t = useT();
  return (
    <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-wide text-accent-dark">
      {t("taster.free")}
    </span>
  );
}
